import { useEffect } from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { SectionHead } from "../components/SectionHead";
import { ClosingCTA } from "./Home";
import { Reveal, stagger, item } from "../lib/motion";
import { VALUES, EXPERIENCE, PROCESS } from "../data";

export default function About() {
  useEffect(() => {
    document.title = "About — PROSPEC Consultancy";
  }, []);

  return (
    <div className="pt-20">
      {/* INTRO */}
      <section className="max-w-container mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28">
        <SectionHead
          eyebrow="About PROSPEC"
          title="A specialist function, deliberately narrow."
          intro="PROSPEC exists because planning and project controls is too important to be a side task on someone's desk. We are not a full-service PMC and not a contractor. We do one thing — programme and controls — and we do it properly, as an outsourced monthly service."
        />
        <div className="grid md:grid-cols-2 gap-10 mt-14">
          <Reveal>
            <p className="text-slate text-lg leading-relaxed">
              Contractors win work on delivery, not on paperwork. But without a dedicated planner,
              the programme quietly decays: the baseline ages, updates slip, and reporting becomes a
              scramble the night before a review. When a delay claim lands, there is nothing solid to
              stand on.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-slate text-lg leading-relaxed">
              PROSPEC fills that gap. We give contractors the planning and controls capability of a
              much larger organisation — the visibility, the early warning, and the defensible record
              — without the cost and commitment of building an in-house team.
            </p>
          </Reveal>
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-sand border-y border-ink/10">
        <div className="max-w-container mx-auto px-6 md:px-12 lg:px-20 py-24 md:py-32">
          <SectionHead eyebrow="Chapter 01 — Values" title="Three principles we hold to." />
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            className="grid md:grid-cols-3 gap-10 mt-16"
          >
            {VALUES.map((v) => (
              <motion.div key={v.n} variants={item} className="border-t border-ink pt-6">
                <span className="font-mono text-gold text-sm">{v.n}</span>
                <h3 className="font-display text-2xl mt-4 mb-3">{v.title}</h3>
                <p className="text-slate leading-relaxed">{v.body}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* FOUNDER PANEL */}
      <section className="max-w-container mx-auto px-6 md:px-12 lg:px-20 py-24 md:py-32">
        <div className="grid lg:grid-cols-12 gap-10 items-stretch">
          <Reveal className="lg:col-span-5">
            <div className="relative h-full min-h-[420px] overflow-hidden border border-ink/10">
              <img
                src="https://images.pexels.com/photos/8961006/pexels-photo-8961006.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=800"
                alt="Project planning professional on site"
                className="absolute inset-0 w-full h-full object-cover grayscale contrast-[1.05]"
              />
              <div className="absolute inset-0 bg-teal/25 mix-blend-multiply" />
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-7">
            <div className="grain relative overflow-hidden bg-ink text-cream p-10 md:p-14 h-full">
              <div className="relative">
                <p className="eyebrow mb-6">Founder background</p>
                <h3 className="font-display text-3xl md:text-4xl text-cream leading-tight mb-8">
                  Rooted in UK infrastructure and civil engineering planning.
                </h3>
                <ul className="space-y-5">
                  {[
                    "Background in UK infrastructure and civil engineering project planning.",
                    "MSc in Construction & Project Management.",
                    "Core discipline in Primavera P6 programme development and control.",
                    "Experience across baseline management, WBS, and critical path & float analysis.",
                    "NEC / Clause 32-style programme reporting on formal contracts.",
                  ].map((line) => (
                    <li key={line} className="flex gap-4">
                      <Check size={20} className="text-gold shrink-0 mt-1" />
                      <span className="text-cream/85 leading-relaxed">{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PROJECT EXPERIENCE */}
      <section className="relative">
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.05] bg-cover bg-center"
          style={{ backgroundImage: "url(https://images.unsplash.com/photo-1709934645644-f055bb2b75de?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600)" }}
        />
        <div className="max-w-container mx-auto px-6 md:px-12 lg:px-20 py-24 md:py-32 relative">
          <SectionHead
            eyebrow="Chapter 02 — Foundations"
            title="The project types behind our methodology."
            intro="These are the sectors the founder's experience covers — the foundation of PROSPEC's approach, not a list of current client projects."
          />
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/10 border border-ink/10 mt-16"
          >
            {EXPERIENCE.map((e) => (
              <motion.div key={e.title} variants={item} className="bg-cream p-8 md:p-10 group transition-colors duration-300 hover:bg-sand">
                <div className="h-px w-8 bg-gold mb-6 group-hover:w-16 transition-all duration-500" />
                <h3 className="font-display text-xl mb-3 leading-snug">{e.title}</h3>
                <p className="text-slate leading-relaxed">{e.body}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* HOW WE WORK TIMELINE */}
      <section className="bg-ink text-cream grain relative overflow-hidden">
        <div className="max-w-container mx-auto px-6 md:px-12 lg:px-20 py-24 md:py-32 relative">
          <SectionHead eyebrow="Chapter 03 — How we work" title="Four steps to a live controls cycle." light />
          <div className="grid md:grid-cols-4 gap-px bg-cream/10 border border-cream/10 mt-16">
            {PROCESS.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.1}>
                <div className="bg-ink p-8 h-full">
                  <span className="font-mono text-gold text-2xl">{p.n}</span>
                  <h3 className="font-display text-xl text-cream mt-5 mb-3 leading-tight">{p.title}</h3>
                  <p className="text-cream/60 leading-relaxed text-sm">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <div className="pt-24 md:pt-32">
        <ClosingCTA />
      </div>
    </div>
  );
}
